import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Field, NativeSelect } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const ENTITY_FIELDS = [["company_name", "Company / legal name"], ["vat_number", "VAT number (P. IVA)"], ["tax_code", "Tax code (Codice fiscale)"],
  ["address", "Billing address"], ["email", "Billing email"], ["phone", "Phone"], ["prefix", "Invoice prefix"], ["default_tax_rate", "Default VAT %"],
  ["exempt_note", "Note when VAT is 0%"], ["bank", "Bank details (IBAN)"]];

function EntityCard({ loc, e, onChange }) {
  return (
    <div className="rounded-[var(--radius)] border bg-card p-5 space-y-3" data-testid={`invoicing-entity-${loc.id}`}>
      <p className="font-semibold">{loc.name} <span className="text-muted-foreground font-normal">({loc.currency})</span></p>
      <div className="grid sm:grid-cols-2 gap-3">
        {ENTITY_FIELDS.map(([k, label]) => (
          <Field key={k} label={label} className={["address", "exempt_note", "bank"].includes(k) ? "sm:col-span-2" : ""}>
            <Input data-testid={`invoicing-${k}-${loc.id}`} type={k === "default_tax_rate" ? "number" : "text"} value={e[k] ?? ""} onChange={(ev) => onChange({ ...e, [k]: ev.target.value })} />
          </Field>
        ))}
      </div>
    </div>
  );
}

export function InvoicingSettings() {
  const { locations } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/invoicing/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    try { setS((await api.put("/invoicing/settings", s)).data); toast.success("Invoice settings saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5 max-w-4xl" data-testid="invoicing-settings">
      <div className="rounded-[var(--radius)] border bg-card p-5 grid sm:grid-cols-3 gap-4 items-end">
        <label className="flex items-center gap-2 text-sm sm:col-span-3"><input type="checkbox" data-testid="invoicing-auto" checked={s.auto} onChange={(e) => setS({ ...s, auto: e.target.checked })} />Create an invoice automatically for every payment (treatments, packages, gift cards, shop orders)</label>
        <Field label="Numbering"><NativeSelect testId="invoicing-numbering" value={s.numbering} onChange={(v) => setS({ ...s, numbering: v })}
          options={[{ value: "location", label: "Separate numbering per location" }, { value: "clinic", label: "One numbering for the whole clinic" }]} /></Field>
        {s.numbering === "clinic" && <Field label="Clinic prefix"><Input data-testid="invoicing-clinic-prefix" value={s.clinic_prefix} onChange={(e) => setS({ ...s, clinic_prefix: e.target.value })} /></Field>}
        <p className="text-xs text-muted-foreground sm:col-span-3">Example number: {s.numbering === "clinic" ? s.clinic_prefix || "INV" : (s.entities[locations[0]?.id]?.prefix || "LV")}-{new Date().getFullYear()}-0001. Numbers restart every year. Credit notes start with CN-.</p>
        <Field label="Footer on every invoice" className="sm:col-span-3"><Input data-testid="invoicing-footer" value={s.footer} onChange={(e) => setS({ ...s, footer: e.target.value })} placeholder="e.g. Thank you for choosing us" /></Field>
      </div>
      {locations.map((l) => <EntityCard key={l.id} loc={l} e={s.entities[l.id] || {}} onChange={(v) => setS({ ...s, entities: { ...s.entities, [l.id]: v } })} />)}
      <p className="text-xs text-muted-foreground">Italy: these are PDF invoices. Official electronic invoices (fattura elettronica via SdI) and Sistema Tessera Sanitaria reporting need an accredited provider; ask your accountant.</p>
      <Button className="rounded-full" data-testid="invoicing-save" onClick={save}>Save invoice settings</Button>
    </div>
  );
}
